import { Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [hoveredLink, setHoveredLink] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { to: "/", label: "Home" },
    { to: "/movies", label: "Movies" },
    { to: "/favorites", label: "Favorites" },
    { to: "/add", label: "Add Movie" },
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 w-full ${
      isScrolled 
        ? 'bg-gray-950/90 backdrop-blur-xl shadow-2xl shadow-black/20 border-b border-white/10' 
        : 'bg-transparent'
    }`}>
      <div className="w-full px-3 sm:px-4 lg:px-8 overflow-hidden">
        <div className="flex items-center justify-between h-16 sm:h-20 w-full">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex-shrink-0 flex items-center space-x-3 group relative"
          >
            <div className="relative">
              <div className="absolute -inset-2 bg-gradient-to-r from-orange-500 to-pink-500 rounded-xl blur-lg opacity-0 group-hover:opacity-40 transition-opacity duration-500"></div>
              <span className="relative text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-400 via-cyan-500 to-teal-500 bg-clip-text text-transparent group-hover:scale-105 transition-transform duration-300 whitespace-nowrap">
                APKnation MovieHub
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-2">
            {navLinks.map((link, index) => (
              <Link
                key={link.to}
                to={link.to}
                onMouseEnter={() => setHoveredLink(index)}
                onMouseLeave={() => setHoveredLink(null)}
                className={`relative px-5 py-2.5 rounded-xl font-medium transition-all duration-300 overflow-hidden group ${
                  isActive(link.to)
                    ? 'text-white'
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                {/* Background gradient on hover */}
                <span className={`absolute inset-0 bg-gradient-to-r from-orange-500/20 to-pink-500/20 rounded-xl transition-all duration-300 ${
                  isActive(link.to) 
                    ? 'opacity-100 bg-gradient-to-r from-orange-500 to-pink-500' 
                    : hoveredLink === index 
                      ? 'opacity-100 scale-100' 
                      : 'opacity-0 scale-95'
                }`}></span>
                
                {/* Glow effect */}
                <span className={`absolute inset-0 rounded-xl transition-all duration-500 ${
                  hoveredLink === index && !isActive(link.to)
                    ? 'shadow-[0_0_20px_rgba(249,115,22,0.4)]'
                    : ''
                }`}></span>
                
                {/* Label */}
                <span className="relative">{link.label}</span>
                
                {/* Underline animation */}
                <span className={`absolute bottom-1 left-5 right-5 h-0.5 bg-gradient-to-r from-orange-400 to-pink-500 rounded-full transition-all duration-300 ${
                  isActive(link.to) 
                    ? 'scale-x-100' 
                    : hoveredLink === index 
                      ? 'scale-x-100' 
                      : 'scale-x-0'
                }`}></span>
              </Link>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden flex-shrink-0 relative p-3 rounded-xl overflow-hidden group"
          >
            {/* Background glow */}
            <span className={`absolute inset-0 bg-gradient-to-r from-orange-500 to-pink-500 rounded-xl transition-opacity duration-300 ${
              isMobileMenuOpen ? 'opacity-20' : 'opacity-0 group-hover:opacity-20'
            }`}></span>
            
            {/* Animated hamburger lines */}
            <div className="relative w-6 h-5 flex flex-col justify-between">
              <span className={`block h-0.5 bg-gradient-to-r from-orange-400 to-pink-500 rounded-full transition-all duration-300 origin-center ${
                isMobileMenuOpen ? 'rotate-45 translate-y-2' : ''
              }`}></span>
              <span className={`block h-0.5 bg-gradient-to-r from-orange-400 to-pink-500 rounded-full transition-all duration-300 ${
                isMobileMenuOpen ? 'opacity-0 scale-x-0' : 'opacity-100 scale-x-100'
              }`}></span>
              <span className={`block h-0.5 bg-gradient-to-r from-orange-400 to-pink-500 rounded-full transition-all duration-300 origin-center ${
                isMobileMenuOpen ? '-rotate-45 -translate-y-2' : ''
              }`}></span>
            </div>
          </button>
        </div>

        {/* Mobile Navigation */}
        <div className={`md:hidden transition-all duration-500 ease-out overflow-hidden ${
          isMobileMenuOpen ? 'max-h-80 opacity-100' : 'max-h-0 opacity-0'
        }`}>
          <div className="py-4 space-y-2 bg-gray-900/95 backdrop-blur-xl rounded-2xl mt-2 border border-white/10 shadow-2xl">
            {navLinks.map((link, index) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`relative block px-4 py-3 rounded-xl text-base font-semibold transition-all duration-300 overflow-hidden group ${
                  isActive(link.to)
                    ? 'text-white bg-gradient-to-r from-orange-500/20 to-pink-500/20'
                    : 'text-gray-200 hover:text-white hover:bg-white/10'
                }`}
                style={{
                  transitionDelay: isMobileMenuOpen ? `${index * 75}ms` : '0ms'
                }}
              >
                {/* Left border indicator */}
                <span className={`absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-full bg-gradient-to-b from-orange-400 to-pink-500 transition-all duration-300 ${
                  isActive(link.to) ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                }`}></span>
                
                {/* Label */}
                <span className="relative ml-3">{link.label}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
